"use client";

import { useEffect, useRef } from "react";
import posthog from "posthog-js";

import { readInternalSource } from "@/lib/analytics/internal-source";
import type { VariantResolutionSource } from "@/lib/products/purchasable-variant";

interface ProductViewedTrackerProps {
	productName: string;
	productSlug: string;
	category?: string | null;
	locale: string;
	channel: string;
	priceAmount?: number | null;
	currency?: string | null;
	selectedVariantId?: string | null;
	selectedDose?: string | null;
	selectedPack?: number | null;
	selectedVariantPurchasable: boolean;
	selectionSource: VariantResolutionSource;
	requestedVariantId?: string | null;
	requestedVariantState?: "valid" | "oos" | "invalid";
}

export function ProductViewedTracker(props: ProductViewedTrackerProps) {
	const lastViewedPage = useRef<string | null>(null);
	const pageIdentity = `${props.locale}:${props.channel}:${props.productSlug}`;

	useEffect(() => {
		if (lastViewedPage.current === pageIdentity) return;
		lastViewedPage.current = pageIdentity;
		// Internal-source attribution: when the PDP was reached from a tool link (e.g. the
		// peptide-calculator buy CTA hands `calculator` + its slug over on click; older links
		// carry `?src=…`), stamp it on product_viewed so a calculator→purchase funnel is
		// queryable by property instead of fragile same-person path stitching. Read
		// client-only to avoid a useSearchParams Suspense/CSR bailout on the PDP.
		const attribution =
			typeof window !== "undefined"
				? readInternalSource(window.location.search, window.location.hash, window.location.pathname)
				: {};
		try {
			posthog.capture("product_viewed", {
				product_name: props.productName,
				product_slug: props.productSlug,
				category: props.category ?? undefined,
				locale: props.locale,
				channel: props.channel,
				price: props.priceAmount ?? undefined,
				currency: props.currency ?? undefined,
				selected_variant_id: props.selectedVariantId ?? undefined,
				dose: props.selectedDose ?? undefined,
				pack_count: props.selectedPack ?? undefined,
				variant_purchasable: props.selectedVariantPurchasable,
				selection_source: props.selectionSource,
				requested_variant_id: props.requestedVariantId ?? undefined,
				requested_variant_state: props.requestedVariantState,
				...attribution,
			});
		} catch {
			// PostHog availability must not affect PDP navigation or URL cleanup.
		}
		const currentUrl = new URL(window.location.href);
		for (const key of ["_requested_variant", "_requested_variant_state", "_variant_selection_source"]) {
			currentUrl.searchParams.delete(key);
		}
		window.history.replaceState(
			window.history.state,
			"",
			`${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
		);
	}, [pageIdentity]); // eslint-disable-line react-hooks/exhaustive-deps

	return null;
}
